import Vue from 'vue';
import { capitalize, fromPairs, isPlainObject } from 'lodash';

export default class Analytics {
    constructor(context) {
        this.sent = [];
        this.unsent = [];
        this.context = context || {};
    }

    event(...args) {
        this.query('send', 'event', ...args);
    }

    time(timingCategory, timingVar, timingLabel) {
        const args = isPlainObject(timingCategory)
            ? timingCategory
            : { timingCategory, timingVar, timingLabel };

        if (!this.context.performance) {
            this.unsent.push({ hitType: 'timing', ...args });
            return;
        }

        const timingValue = Math.round(this.context.performance.now());
        this.timing({ ...args, timingValue });
    }

    timing(...args) {
        this.query('send', 'timing', ...args);
    }

    query(...args) {
        if (!this.context.ga) {
            this.unsent.push({ ...args });
            return;
        }

        this.sent.push({ ...args });
        this.context.ga(...args);
    }
}

const $ga = new Analytics(window);
Vue.$ga = $ga;
Vue.prototype.$ga = $ga;

// Handle Element UI components since inputs are children
const getDomElement = (element, component) => {
    if (!component) {
        return element;
    }

    const componentName = component.$options.name || '';
    if (componentName.startsWith('El')) {
        return element.querySelector('input, textarea');
    }

    return element;
};

const setGaAttributes = (element, category, action) => {
    const attrs = isPlainObject(action) ? action : { category, action };
    Object.entries(attrs).forEach(([attr, value]) => {
        if (value === null || value === undefined) {
            return;
        }

        element.setAttribute(`ga-${attr}`, value);
    });
};

// Directive for adding GA events to components
//     https://developers.google.com/analytics/devguides/collection/analyticsjs/events
//     https://developers.google.com/analytics/devguides/collection/analyticsjs/user-timings
// Usage: v-ga:{type}.{events}="{ props }"
// Examples:
//     v-ga="{ category: 'Foo', action: 'bar' }" // send event on click
//     v-ga.focus.blur="{ category: 'Foo', action: 'bar' }" // send event on focus and blur
//     v-ga:time="{ category: 'Foo', var: 'bar' }" // send timing on click
Vue.directive('ga', {
    inserted: (el, { arg, modifiers, value }, { componentInstance }) => {
        const type = arg || 'event';
        const element = getDomElement(el, componentInstance);
        const events = Object.keys(modifiers);
        if (events.length === 0) {
            events.push('click');
        }

        setGaAttributes(el, arg, value);

        events.forEach((event) => element.addEventListener(event, () => {
            const attrs = el.getAttributeNames()
                .filter((name) => name.startsWith('ga-'))
                .map((attr) => {
                    const field = capitalize(attr.substr(3));

                    return [type + field, el.getAttribute(attr)];
                });

            $ga[type](fromPairs(attrs));
        }));
    },
    update: (el, { arg, value }) => {
        setGaAttributes(el, arg, value);
    },
});
